/**
 * MarkdownEditor — Edit/Preview toggle for `.md` files (Task 12 slice).
 *
 * Acceptance (Task 12):
 *   - Toolbar Edit/Preview toggle.
 *   - Source-mode editor for the markdown buffer.
 *   - Save (PUT /api/file) sends `{path, content, expectedMtime, expectedHash}`.
 *   - On 409 (CONFLICT) the structured diff is surfaced through `onConflict`
 *     so Task 13's dialog can pick it up. The editor itself does not render
 *     the dialog — that's a separate component owned by Task 13.
 *   - When `fsWritable=false` the Edit button is hidden entirely; the
 *     component renders preview-only and refuses to enter edit mode even
 *     if a caller passes `initialMode="edit"`.
 *
 * Editor implementation note (CodeMirror 6, FR-3.3):
 *   Source mode is a CodeMirror 6 `EditorView` with syntax highlighting
 *   for markdown (lezer markdown), YAML frontmatter
 *   (`@codemirror/lang-yaml`'s `yamlFrontmatter` wrapper), and fenced code
 *   blocks (`@codemirror/language-data` lazy language registry). CM6 needs
 *   a real DOM, so the view mounts in a client-side effect; until it does
 *   (and always under SSR / node-env tests) a functional `<textarea>`
 *   fallback renders the same buffer, so the component stays usable and
 *   server-renderable. Draft persistence, dirty reporting, and conflict
 *   rebase all operate on the shared `buffer` state — the CM view is a
 *   controlled projection of it.
 */

import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { yamlFrontmatter } from '@codemirror/lang-yaml';
import { HighlightStyle, bracketMatching, syntaxHighlighting } from '@codemirror/language';
import { languages } from '@codemirror/language-data';
import { EditorState, Prec, type Extension } from '@codemirror/state';
import {
  EditorView,
  drawSelection,
  highlightActiveLine,
  keymap,
  lineNumbers,
} from '@codemirror/view';
import { tags } from '@lezer/highlight';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import { renderMathIn } from '../lib/math.js';

/* ── Types mirroring the server contract ─────────────────────────────── */

export interface MarkdownEditorPayload {
  readonly path: string;
  readonly raw: string;
  readonly html: string;
  readonly sha256: string;
  readonly mtimeMs: number;
  readonly size: number;
}

/** Successful write response — what the server returns on 200. */
export interface MarkdownWriteSuccess {
  readonly path: string;
  readonly mtimeMs: number;
  readonly sha256: string;
  readonly size: number;
}

/** Conflict payload mirroring `WriteMdConflictError`. */
export interface MarkdownWriteConflict {
  readonly code: 'CONFLICT';
  readonly path: string;
  readonly expectedMtime: number;
  readonly actualMtime: number;
  readonly expectedHash: string;
  readonly actualHash: string;
  readonly diff: { readonly ours: string; readonly theirs: string };
}

/** Generic write failure (non-conflict). */
export interface MarkdownWriteError {
  readonly code: string;
  readonly message: string;
  readonly path?: string;
}

export type MarkdownWriteResult =
  | { readonly ok: true; readonly value: MarkdownWriteSuccess }
  | { readonly ok: false; readonly conflict: MarkdownWriteConflict }
  | { readonly ok: false; readonly error: MarkdownWriteError };

export type EditorMode = 'preview' | 'edit';

export interface EditorHandle {
  /** Imperatively focus the textarea (used by the conflict-dialog "Re-edit" action in Task 13). */
  focus(): void;
  /** Imperatively set the buffer (used by "Reload from disk" / "Overwrite"). */
  setValue(next: string): void;
  /**
   * Replace the buffer AND advance the baseline atomically. Used by the
   * conflict dialog's "Reload and re-edit" action: after the user picks
   * theirs, subsequent saves must use the fresh on-disk mtime/hash.
   */
  applyResolution(next: {
    readonly content: string;
    readonly mtimeMs: number;
    readonly sha256: string;
  }): void;
  /** Read the current buffer (used by the conflict dialog if needed). */
  getBuffer(): string;
}

export interface MarkdownEditorProps {
  readonly payload: MarkdownEditorPayload;
  /** When false, edit affordances are hidden and saves are refused locally. */
  readonly fsWritable: boolean;
  /** Initial mode. Defaults to 'preview'. Ignored when `fsWritable=false` (forced to 'preview'). */
  readonly initialMode?: EditorMode;
  /**
   * Save the buffer. Implementation defaults to `PUT /api/file` on the
   * same origin; tests inject a stub. Must surface conflicts as
   * `{ok:false, conflict}` so the editor can keep the user's draft.
   */
  readonly saveFile?: (req: {
    readonly path: string;
    readonly content: string;
    readonly expectedMtime: number;
    readonly expectedHash: string;
  }) => Promise<MarkdownWriteResult>;
  /** Called with the structured conflict payload (Task 13's dialog hook). */
  readonly onConflict?: (conflict: MarkdownWriteConflict) => void;
  /** Called on successful save with the refreshed metadata. */
  readonly onSaved?: (next: MarkdownWriteSuccess) => void;
  /** Called on any non-conflict save failure. */
  readonly onError?: (err: MarkdownWriteError) => void;
  /**
   * Dirty-state reporting (FR-11.3 guardrails). The Shell uses this to
   * confirm before closing a dirty tab and to install a beforeunload
   * guard while any buffer has unsaved changes.
   */
  readonly onDirtyChange?: (dirty: boolean) => void;
}

/* ── Draft store (FR-11.3) ───────────────────────────────────────────── */

interface EditorDraft {
  readonly buffer: string;
  readonly baseline: { readonly raw: string; readonly mtimeMs: number; readonly sha256: string };
}

/**
 * Module-level store of unsaved buffers keyed by vault path. The editor
 * unmounts on tab switches; without this, a draft would silently vanish
 * the moment the user clicks another tab. Cleared on successful save,
 * explicit reload-from-disk, or confirmed tab close.
 */
const draftStore = new Map<string, EditorDraft>();

export function storeDraft(path: string, draft: EditorDraft): void {
  draftStore.set(path, draft);
}
export function getDraft(path: string): EditorDraft | undefined {
  return draftStore.get(path);
}
export function clearDraft(path: string): void {
  draftStore.delete(path);
}
export function hasDraft(path: string): boolean {
  const d = draftStore.get(path);
  return d !== undefined && d.buffer !== d.baseline.raw;
}

/* ── Default save implementation (real PUT /api/file) ────────────────── */

/**
 * Default `saveFile` — issues `PUT /api/file` and narrows the response
 * onto `MarkdownWriteResult`. Exported for tests and for re-use by the
 * conflict dialog (which needs the same call for "Overwrite").
 */
export async function putMarkdownFile(req: {
  readonly path: string;
  readonly content: string;
  readonly expectedMtime: number;
  readonly expectedHash: string;
  readonly apiBase?: string;
}): Promise<MarkdownWriteResult> {
  const base = (req.apiBase ?? '').replace(/\/+$/, '');
  let res: Response;
  try {
    res = await fetch(`${base}/api/file`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({
        path: req.path,
        content: req.content,
        expectedMtime: req.expectedMtime,
        expectedHash: req.expectedHash,
      }),
    });
  } catch (err) {
    return {
      ok: false,
      error: { code: 'NETWORK', message: err instanceof Error ? err.message : String(err) },
    };
  }
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }
  if (res.status === 200 && parsed && typeof parsed === 'object') {
    const ok = parsed as MarkdownWriteSuccess;
    return { ok: true, value: ok };
  }
  if (
    res.status === 409 &&
    parsed &&
    typeof parsed === 'object' &&
    'error' in parsed &&
    (parsed as { error: { code?: string } }).error?.code === 'CONFLICT'
  ) {
    return {
      ok: false,
      conflict: (parsed as { error: MarkdownWriteConflict }).error,
    };
  }
  const err = (parsed && typeof parsed === 'object' && 'error' in parsed
    ? (parsed as { error: MarkdownWriteError }).error
    : null) ?? {
    code: `HTTP_${res.status}`,
    message: `unexpected ${res.status} response`,
  };
  return { ok: false, error: err };
}

/* ── Source-mode editor (CodeMirror 6, FR-3.3) ───────────────────────── */

/** Dark-theme-token highlight style. Colors lean on the theme CSS vars
 *  where sensible and fall back to Obsidian-default-dark hues. */
const owHighlightStyle = HighlightStyle.define([
  { tag: tags.heading, color: 'var(--text-accent, #7f6df2)', fontWeight: 'bold' },
  { tag: tags.strong, fontWeight: 'bold' },
  { tag: tags.emphasis, fontStyle: 'italic' },
  { tag: tags.strikethrough, textDecoration: 'line-through' },
  { tag: tags.link, color: 'var(--text-accent, #7f6df2)', textDecoration: 'underline' },
  { tag: tags.url, color: 'var(--text-accent, #7f6df2)' },
  { tag: tags.quote, color: 'var(--text-muted, #a1a1aa)', fontStyle: 'italic' },
  { tag: tags.monospace, color: '#98c379' },
  { tag: tags.keyword, color: '#c678dd' },
  { tag: tags.string, color: '#98c379' },
  { tag: tags.number, color: '#d19a66' },
  { tag: tags.comment, color: 'var(--text-faint, #6b6b6b)', fontStyle: 'italic' },
  { tag: tags.typeName, color: '#e5c07b' },
  { tag: tags.variableName, color: '#e06c75' },
  { tag: tags.propertyName, color: '#61afef' },
  { tag: tags.meta, color: 'var(--text-faint, #6b6b6b)' },
  { tag: tags.processingInstruction, color: 'var(--text-faint, #6b6b6b)' },
  { tag: tags.contentSeparator, color: 'var(--text-faint, #6b6b6b)' },
]);

const owEditorTheme = EditorView.theme(
  {
    '&': {
      height: '100%',
      fontSize: '13px',
      backgroundColor: 'var(--background-primary, #1e1e1e)',
      color: 'var(--text-normal, #dcddde)',
    },
    '.cm-content': {
      fontFamily: 'var(--font-monospace, ui-monospace, SFMono-Regular, Menlo, monospace)',
      caretColor: 'var(--text-normal, #dcddde)',
    },
    '.cm-gutters': {
      backgroundColor: 'var(--background-secondary, #161616)',
      color: 'var(--text-faint, #6b6b6b)',
      borderRight: '1px solid var(--background-modifier-border, #333)',
    },
    '.cm-activeLine': { backgroundColor: 'rgba(255,255,255,0.04)' },
    '&.cm-focused': { outline: 'none' },
    '.cm-scroller': { overflow: 'auto' },
  },
  { dark: true },
);

/**
 * CodeMirror extension stack for the markdown source editor (FR-3.3):
 * markdown syntax (lezer), YAML frontmatter, lazily-loaded fenced-code
 * languages, line numbers, history, and a Mod-S save binding.
 * Exported so tests can assert the language composition without a DOM.
 */
export function buildSourceExtensions(opts: {
  readonly readOnly: boolean;
  readonly onChange: (next: string) => void;
  readonly onSaveShortcut: () => void;
}): Extension[] {
  return [
    lineNumbers(),
    history(),
    drawSelection(),
    highlightActiveLine(),
    bracketMatching(),
    yamlFrontmatter({
      content: markdown({ base: markdownLanguage, codeLanguages: languages }),
    }),
    syntaxHighlighting(owHighlightStyle, { fallback: true }),
    owEditorTheme,
    EditorState.tabSize.of(2),
    EditorState.readOnly.of(opts.readOnly),
    EditorView.editable.of(!opts.readOnly),
    EditorView.lineWrapping,
    Prec.highest(
      keymap.of([
        {
          key: 'Mod-s',
          preventDefault: true,
          run: () => {
            opts.onSaveShortcut();
            return true;
          },
        },
      ]),
    ),
    keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
    EditorView.updateListener.of((update) => {
      if (update.docChanged) opts.onChange(update.state.doc.toString());
    }),
  ];
}

export interface SourceEditorHandle {
  focus(): void;
}

interface SourceEditorProps {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly readOnly: boolean;
  readonly onSaveShortcut: () => void;
}

/**
 * The CM6 mount. Renders a fallback `<textarea>` (SSR / pre-mount / DOM-less
 * test environments) that is swapped for the CodeMirror view once the
 * client effect runs.
 */
const SourceEditor = forwardRef<SourceEditorHandle, SourceEditorProps>(
  function SourceEditor(props, ref) {
    const { value, onChange, readOnly, onSaveShortcut } = props;
    const hostRef = useRef<HTMLDivElement | null>(null);
    const viewRef = useRef<EditorView | null>(null);
    const fallbackRef = useRef<HTMLTextAreaElement | null>(null);
    const [cmActive, setCmActive] = useState(false);

    // Latest callbacks without re-creating the view per render.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const onSaveRef = useRef(onSaveShortcut);
    onSaveRef.current = onSaveShortcut;
    // Buffer text as CM last reported it — used to distinguish "user typed
    // in CM" from "the buffer was replaced from outside".
    const cmDocRef = useRef(value);

    useImperativeHandle(
      ref,
      (): SourceEditorHandle => ({
        focus: () => {
          if (viewRef.current) viewRef.current.focus();
          else fallbackRef.current?.focus();
        },
      }),
      [],
    );

    // Mount the CodeMirror view (client only — jsdom-less SSR skips this).
    useEffect(() => {
      const host = hostRef.current;
      if (!host || typeof document === 'undefined') return undefined;
      const view = new EditorView({
        state: EditorState.create({
          doc: cmDocRef.current,
          extensions: buildSourceExtensions({
            readOnly,
            onChange: (next) => {
              cmDocRef.current = next;
              onChangeRef.current(next);
            },
            onSaveShortcut: () => onSaveRef.current(),
          }),
        }),
        parent: host,
      });
      viewRef.current = view;
      setCmActive(true);
      return () => {
        viewRef.current = null;
        view.destroy();
        setCmActive(false);
      };
      // `readOnly` is deliberately not a dependency: it is fixed for the
      // lifetime of an edit-mode mount (the editor unmounts when
      // fsWritable flips) and recreating the view would lose CM history.
    }, []);

    // Push external buffer replacements (draft restore, reload-from-disk,
    // conflict resolution) into the view.
    useEffect(() => {
      const view = viewRef.current;
      if (!view) return;
      if (value === cmDocRef.current) return;
      cmDocRef.current = value;
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: value },
      });
    }, [value]);

    return (
      <div
        className="ow-editor-source"
        data-testid="markdown-editor-source"
        style={{
          height: '100%',
          background: 'var(--background-primary, #1e1e1e)',
          border: '1px solid var(--background-modifier-border, #333)',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <div
          ref={hostRef}
          data-testid="markdown-editor-cm"
          style={{ height: '100%', display: cmActive ? 'block' : 'none' }}
        />
        {cmActive ? null : (
          <SourceTextarea
            ref={fallbackRef}
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            onSaveShortcut={onSaveShortcut}
          />
        )}
      </div>
    );
  },
);

/* ── Fallback source textarea (SSR / pre-CM mount) ────────────────────── */

interface SourceTextareaProps {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly readOnly: boolean;
  readonly onSaveShortcut: () => void;
}

const SourceTextarea = forwardRef<HTMLTextAreaElement, SourceTextareaProps>(
  function SourceTextarea(props, ref) {
    const { value, onChange, readOnly, onSaveShortcut } = props;

    // Compute line numbers for the gutter. Memoised so we only recompute on
    // buffer change rather than on every keystroke render.
    const lineCount = useMemo(() => {
      let n = 1;
      for (let i = 0; i < value.length; i++) if (value.charCodeAt(i) === 10) n++;
      return n;
    }, [value]);

    const handleKeyDown = useCallback(
      (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
        // Cmd/Ctrl+S → save (matches Obsidian-desktop binding).
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
          e.preventDefault();
          onSaveShortcut();
          return;
        }
        // Tab inserts two spaces (CM6 default for markdown).
        if (e.key === 'Tab' && !e.shiftKey) {
          e.preventDefault();
          const ta = e.currentTarget;
          const start = ta.selectionStart;
          const end = ta.selectionEnd;
          const next = value.slice(0, start) + '  ' + value.slice(end);
          onChange(next);
          // Restore caret after the inserted indent on the next paint.
          requestAnimationFrame(() => {
            ta.selectionStart = ta.selectionEnd = start + 2;
          });
        }
      },
      [onChange, onSaveShortcut, value],
    );

    return (
      <div
        className="ow-editor-fallback"
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: 0,
          fontFamily: 'var(--font-monospace, ui-monospace, SFMono-Regular, Menlo, monospace)',
          fontSize: 13,
          lineHeight: '1.5em',
          background: 'var(--background-primary, #1e1e1e)',
          color: 'var(--text-normal, #dcddde)',
          overflow: 'hidden',
          height: '100%',
        }}
      >
        <div
          className="ow-editor-gutter"
          aria-hidden="true"
          data-testid="markdown-editor-gutter"
          style={{
            padding: '8px 8px 8px 12px',
            color: 'var(--text-faint, #6b6b6b)',
            background: 'var(--background-secondary, #161616)',
            textAlign: 'right',
            userSelect: 'none',
            borderRight: '1px solid var(--background-modifier-border, #333)',
            whiteSpace: 'pre',
          }}
        >
          {Array.from({ length: lineCount }, (_, i) => i + 1).join('\n')}
        </div>
        <textarea
          ref={ref}
          className="ow-editor-textarea"
          data-testid="markdown-editor-textarea"
          spellCheck={false}
          readOnly={readOnly}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            height: '100%',
            minHeight: 200,
            padding: 8,
            margin: 0,
            border: 'none',
            outline: 'none',
            resize: 'none',
            background: 'transparent',
            color: 'inherit',
            font: 'inherit',
            lineHeight: 'inherit',
            tabSize: 2,
            whiteSpace: 'pre',
            overflow: 'auto',
          }}
        />
      </div>
    );
  },
);

/* ── Toolbar ─────────────────────────────────────────────────────────── */

interface ToolbarProps {
  readonly mode: EditorMode;
  readonly fsWritable: boolean;
  readonly dirty: boolean;
  readonly saving: boolean;
  readonly lastSavedMtime: number;
  readonly onModeChange: (mode: EditorMode) => void;
  readonly onSave: () => void;
}

function Toolbar(props: ToolbarProps): JSX.Element {
  const showEdit = props.fsWritable;
  return (
    <div
      className="ow-editor-toolbar"
      data-testid="markdown-editor-toolbar"
      role="toolbar"
      aria-label="Markdown editor toolbar"
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        padding: '6px 8px',
        borderBottom: '1px solid var(--background-modifier-border, #333)',
        background: 'var(--background-secondary, #161616)',
        fontSize: 12,
      }}
    >
      {showEdit ? (
        <div role="tablist" aria-label="Mode" style={{ display: 'flex', gap: 4 }}>
          <button
            type="button"
            role="tab"
            aria-selected={props.mode === 'preview'}
            data-testid="markdown-editor-mode-preview"
            className={props.mode === 'preview' ? 'active' : ''}
            onClick={() => props.onModeChange('preview')}
          >
            Preview
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={props.mode === 'edit'}
            data-testid="markdown-editor-mode-edit"
            className={props.mode === 'edit' ? 'active' : ''}
            onClick={() => props.onModeChange('edit')}
          >
            Edit
          </button>
        </div>
      ) : (
        <span
          data-testid="markdown-editor-readonly-badge"
          style={{
            color: 'var(--text-muted, #a1a1aa)',
            background: 'var(--background-modifier-border, #2a2a2a)',
            padding: '2px 6px',
            borderRadius: 3,
          }}
          title="The server is running with FS_WRITABLE=false; editing is disabled."
        >
          Read-only
        </span>
      )}
      <span style={{ flex: 1 }} />
      {props.dirty && props.fsWritable ? (
        <span data-testid="markdown-editor-dirty" style={{ color: 'var(--text-accent, #7f6df2)' }}>
          Unsaved changes
        </span>
      ) : null}
      {props.fsWritable ? (
        <button
          type="button"
          data-testid="markdown-editor-save"
          disabled={!props.dirty || props.saving || props.mode !== 'edit'}
          onClick={props.onSave}
        >
          {props.saving ? 'Saving…' : 'Save'}
        </button>
      ) : null}
      <span
        data-testid="markdown-editor-mtime"
        style={{ color: 'var(--text-faint, #6b6b6b)', fontVariantNumeric: 'tabular-nums' }}
      >
        mtime {Math.round(props.lastSavedMtime)}
      </span>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────────── */

export const MarkdownEditor = forwardRef<EditorHandle, MarkdownEditorProps>(
  function MarkdownEditor(props, ref) {
    const { payload, fsWritable } = props;
    const initialMode: EditorMode = !fsWritable ? 'preview' : (props.initialMode ?? 'preview');

    const [mode, setMode] = useState<EditorMode>(initialMode);
    // Restore a surviving draft for this path (tab switch round-trip).
    const [buffer, setBuffer] = useState<string>(
      () => getDraft(payload.path)?.buffer ?? payload.raw,
    );
    const [baseline, setBaseline] = useState<{
      readonly raw: string;
      readonly mtimeMs: number;
      readonly sha256: string;
    }>(
      () =>
        getDraft(payload.path)?.baseline ?? {
          raw: payload.raw,
          mtimeMs: payload.mtimeMs,
          sha256: payload.sha256,
        },
    );
    // FR-11.3: true when the on-disk file changed underneath a dirty
    // buffer — renders the "File changed on disk" reload banner.
    const [externalNotice, setExternalNotice] = useState<boolean>(() => {
      const draft = getDraft(payload.path);
      return draft !== undefined && draft.baseline.sha256 !== payload.sha256;
    });
    const [saving, setSaving] = useState<boolean>(false);
    const sourceRef = useRef<SourceEditorHandle | null>(null);
    const previewRef = useRef<HTMLElement | null>(null);

    // US-02: typeset the server-emitted `.math` placeholders with KaTeX
    // whenever the rendered preview (re)mounts or its HTML changes.
    // renderMathIn is idempotent, so re-runs are cheap no-ops.
    useEffect(() => {
      const el = previewRef.current;
      if (el) renderMathIn(el);
    });

    // Keep the live buffer/baseline addressable from effects and imperative
    // handle callers without re-subscribing on every keystroke.
    const bufferRef = useRef(buffer);
    bufferRef.current = buffer;
    const baselineRef = useRef(baseline);
    baselineRef.current = baseline;

    // Reset the buffer when the file payload changes (user switched tabs or
    // an external write came in via the watcher hook). When the SAME file's
    // payload changes underneath a dirty buffer (external edit while the
    // user is typing) we keep the draft AND the old baseline: the next save
    // then 409s and the conflict dialog resolves it explicitly — never
    // silently discard the user's work.
    const prevPathRef = useRef(payload.path);
    useEffect(() => {
      const pathChanged = prevPathRef.current !== payload.path;
      prevPathRef.current = payload.path;
      if (pathChanged) {
        const draft = getDraft(payload.path);
        if (draft && draft.buffer !== payload.raw) {
          // Tab switched back onto a path with a surviving draft.
          setBuffer(draft.buffer);
          setBaseline(draft.baseline);
          setExternalNotice(draft.baseline.sha256 !== payload.sha256);
        } else {
          setBuffer(payload.raw);
          setBaseline({ raw: payload.raw, mtimeMs: payload.mtimeMs, sha256: payload.sha256 });
          setExternalNotice(false);
        }
        return;
      }
      if (bufferRef.current !== baselineRef.current.raw) {
        // Same file, dirty buffer, new payload → the file changed on disk
        // out from under the user. Keep the draft, show the reload banner.
        if (payload.sha256 !== baselineRef.current.sha256) setExternalNotice(true);
        return;
      }
      setBuffer(payload.raw);
      setBaseline({ raw: payload.raw, mtimeMs: payload.mtimeMs, sha256: payload.sha256 });
      setExternalNotice(false);
    }, [payload.path, payload.raw, payload.mtimeMs, payload.sha256]);

    // Persist the draft on every buffer/baseline change so it survives
    // unmount (tab switch); drop it once the buffer matches the baseline.
    useEffect(() => {
      if (prevPathRef.current !== payload.path) return; // mid-switch render
      if (buffer !== baseline.raw) {
        storeDraft(payload.path, { buffer, baseline });
      } else {
        clearDraft(payload.path);
      }
    }, [buffer, baseline, payload.path]);

    // Report dirty transitions upward (tab-close confirm / beforeunload).
    const dirtyNow = buffer !== baseline.raw;
    const onDirtyChange = props.onDirtyChange;
    useEffect(() => {
      onDirtyChange?.(dirtyNow);
    }, [dirtyNow, payload.path, onDirtyChange]);

    // Force preview mode when read-only flips on.
    useEffect(() => {
      if (!fsWritable && mode === 'edit') setMode('preview');
    }, [fsWritable, mode]);

    useImperativeHandle(
      ref,
      (): EditorHandle => ({
        focus: () => sourceRef.current?.focus(),
        setValue: (next: string) => setBuffer(next),
        applyResolution: (next) => {
          // Replace the buffer AND advance the baseline so the next save
          // uses the fresh on-disk mtime/hash. This is the post-"Reload"
          // and post-successful-"Overwrite" path. Without rebasing the
          // baseline, the editor would immediately re-conflict on the
          // next save.
          setBuffer(next.content);
          setBaseline({ raw: next.content, mtimeMs: next.mtimeMs, sha256: next.sha256 });
          clearDraft(payload.path);
          setExternalNotice(false);
        },
        getBuffer: () => bufferRef.current,
      }),
      [payload.path],
    );

    // FR-3.4: Ctrl/Cmd+E toggles Reading ⇄ Source view while a markdown
    // file is open. Global (not textarea-scoped) so it works from Reading
    // view too. No-op when the server is read-only.
    useEffect(() => {
      if (typeof window === 'undefined' || !fsWritable) return undefined;
      const onKey = (e: KeyboardEvent): void => {
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'e') {
          e.preventDefault();
          setMode((prev) => (prev === 'edit' ? 'preview' : 'edit'));
        }
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }, [fsWritable]);

    const dirty = buffer !== baseline.raw;

    const doSave = useCallback(async () => {
      if (!fsWritable) return;
      if (!dirty || saving) return;
      setSaving(true);
      try {
        const save = props.saveFile ?? putMarkdownFile;
        const result = await save({
          path: payload.path,
          content: buffer,
          expectedMtime: baseline.mtimeMs,
          expectedHash: baseline.sha256,
        });
        if (result.ok) {
          setBaseline({
            raw: buffer,
            mtimeMs: result.value.mtimeMs,
            sha256: result.value.sha256,
          });
          clearDraft(payload.path);
          setExternalNotice(false);
          props.onSaved?.(result.value);
        } else if ('conflict' in result) {
          props.onConflict?.(result.conflict);
        } else {
          props.onError?.(result.error);
        }
      } finally {
        setSaving(false);
      }
    }, [baseline.mtimeMs, baseline.sha256, buffer, dirty, fsWritable, payload.path, props, saving]);

    const handleModeChange = useCallback(
      (next: EditorMode) => {
        if (next === 'edit' && !fsWritable) return;
        setMode(next);
      },
      [fsWritable],
    );

    return (
      <section
        className="ow-markdown-editor"
        data-testid="markdown-editor"
        data-mode={mode}
        data-fs-writable={fsWritable ? 'true' : 'false'}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Toolbar
          mode={mode}
          fsWritable={fsWritable}
          dirty={dirty}
          saving={saving}
          lastSavedMtime={baseline.mtimeMs}
          onModeChange={handleModeChange}
          onSave={() => {
            void doSave();
          }}
        />
        {externalNotice && dirty ? (
          <div
            data-testid="markdown-editor-reload-banner"
            role="alert"
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              padding: '6px 10px',
              background: 'var(--background-modifier-error, #3b1d1d)',
              color: 'var(--text-normal, #dcddde)',
              borderBottom: '1px solid var(--background-modifier-border, #333)',
              fontSize: 12,
            }}
          >
            <span>
              File changed on disk while you were editing. Reload to pick up the on-disk
              version (discards your draft) or keep editing — the next save will open the
              conflict dialog.
            </span>
            <button
              type="button"
              data-testid="markdown-editor-reload-from-disk"
              onClick={() => {
                setBuffer(payload.raw);
                setBaseline({ raw: payload.raw, mtimeMs: payload.mtimeMs, sha256: payload.sha256 });
                clearDraft(payload.path);
                setExternalNotice(false);
              }}
            >
              Reload from disk
            </button>
            <button
              type="button"
              data-testid="markdown-editor-keep-draft"
              onClick={() => setExternalNotice(false)}
            >
              Keep my draft
            </button>
          </div>
        ) : null}
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {mode === 'edit' && fsWritable ? (
            <SourceEditor
              ref={sourceRef}
              value={buffer}
              onChange={setBuffer}
              readOnly={false}
              onSaveShortcut={() => {
                void doSave();
              }}
            />
          ) : (
            <article
              ref={previewRef}
              className="ow-markdown-viewer"
              data-testid="markdown-editor-preview"
              // The server already escaped untrusted text in `html`; we trust
              // it the same way the read-side viewer does.
              dangerouslySetInnerHTML={{ __html: payload.html }}
              style={{ padding: '16px 20px', overflow: 'auto', height: '100%' }}
            />
          )}
        </div>
      </section>
    );
  },
);

export default MarkdownEditor;
